/// <reference types="google.maps" />
import { useEffect, useRef, useState } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { Input } from "@/components/ui/input";
import { MapPin } from "lucide-react";

export interface PlaceAddressData {
  region: string;
  city: string;
  district: string;
  streetName: string;
  buildingNumber: string;
  postalCode: string;
  latitude: number | null;
  longitude: number | null;
  formattedAddress: string;
}

interface PlacesAutocompleteProps {
  onPlaceSelect: (data: PlaceAddressData) => void;
  placeholder?: string;
  language?: string;
  className?: string;
  externalValue?: string;
  /** data-testid on the input (E2E hook). */
  inputTestId?: string;
  /**
   * Fired on every keystroke, mirroring the plain-input fallback in
   * AddressSearchField. Without this, the form's `address` field is ONLY
   * ever set via the `place_changed` listener below — which depends on
   * Google's proprietary Autocomplete widget reliably firing for the click
   * that lands on a `.pac-item` suggestion. Per the qa-daily CI trace
   * evidence for issue #2892, a real `.pac-item` click can still leave
   * `address` empty in the form (place_changed never fired) even though the
   * input visibly shows the typed text — silently breaking the Tab-1 →
   * Tab-2 handoff in AddPropertyDialog ("Add unit" sub-form unreachable).
   * Treat the Places
   * suggestion as an ENHANCEMENT (fills city/district/lat/lng too) on top
   * of the typed text as the source of truth, not the only path to a
   * non-empty address.
   */
  onChange?: (value: string) => void;
}

export function getComponent(
  components: google.maps.GeocoderAddressComponent[],
  type: string
): string {
  return components.find((c) => c.types.includes(type))?.long_name || "";
}

export function PlacesAutocomplete({
  onPlaceSelect,
  placeholder,
  language = "ar",
  className,
  externalValue,
  inputTestId,
  onChange,
}: PlacesAutocompleteProps) {
  const places = useMapsLibrary("places");
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [inputValue, setInputValue] = useState("");
  // Ref so the place_changed effect (below) doesn't need onChange in its
  // deps — re-creating the Autocomplete instance on every keystroke would
  // drop/duplicate listeners.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Sync external value (e.g. from map click) into the input
  useEffect(() => {
    if (externalValue !== undefined && externalValue !== inputValue) {
      setInputValue(externalValue);
    }
    // Only react to externalValue changes, not inputValue
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalValue]);

  useEffect(() => {
    if (!places || !inputRef.current) return;

    const autocomplete = new places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: "sa" },
      fields: ["address_components", "geometry", "formatted_address"],
      types: ["geocode"],
    });

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (!place.address_components) return;

      const components = place.address_components;
      const lat = place.geometry?.location?.lat() ?? null;
      const lng = place.geometry?.location?.lng() ?? null;

      const data: PlaceAddressData = {
        region: getComponent(components, "administrative_area_level_1"),
        city: getComponent(components, "locality") ||
          getComponent(components, "administrative_area_level_2"),
        district: getComponent(components, "sublocality_level_1") ||
          getComponent(components, "sublocality") ||
          getComponent(components, "neighborhood"),
        streetName: getComponent(components, "route"),
        buildingNumber: getComponent(components, "street_number"),
        postalCode: getComponent(components, "postal_code"),
        latitude: lat,
        longitude: lng,
        formattedAddress: place.formatted_address || "",
      };

      setInputValue(place.formatted_address || "");
      onChangeRef.current?.(place.formatted_address || "");
      onPlaceSelect(data);
    });

    autocompleteRef.current = autocomplete;

    return () => {
      google.maps.event.clearInstanceListeners(autocomplete);
    };
    // language is set at APIProvider level, not here — no need to re-create on language change
     
  }, [places, onPlaceSelect]);

  return (
    <div className="relative">
      <MapPin className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
      <Input
        ref={inputRef}
        data-testid={inputTestId}
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          // Commit typed text to the form immediately — don't rely solely on
          // Google's place_changed firing (see the onChange prop doc above).
          onChange?.(e.target.value);
        }}
        placeholder={placeholder}
        className={`ps-9 h-9 text-sm ${className || ""}`}
      />
    </div>
  );
}
